import { NOTE_NAMES } from './consts';

import type { NoteInfo } from './types';

/**
 * Frequency → MIDI number, **unrounded**. The rounding half (`freqToNote`) is what
 * names a note; measurements that compare pitches need the fraction, and rounding
 * first turns a 49-cent drift into no drift at all.
 */
export const freqToMidi = (f: number): number => 69 + 12 * Math.log2(f / 440);

/**
 * f0 readings → cents around `centreHz`, each moved by whole octaves to the octave
 * nearest the previous reading.
 *
 * The raw f0 carries the occasional octave error (the native engine only suppresses
 * them on the display-smoothed value). Frame to frame a voice moves far less than half
 * an octave (a ±120 cents vibrato at 6 Hz steps under 100 cents per 21 ms frame), so
 * only a jump of an octave is taken out — an error frame and a real octave change
 * alike, and either way the movement on both sides survives. Folding around a fixed
 * centre instead breaks on a real octave change: with equal halves the median sits
 * between the two notes, both halves land near the ±600 fold line, and a normal swing
 * crosses it (a ±80 cents vibrato read as ±441).
 */
export const unwrappedCents = (f0Hz: number[], centreHz: number): number[] => {
  const cents: number[] = [];
  for (const f of f0Hz) {
    const raw = 1200 * Math.log2(f / centreHz);
    const previous = cents.length > 0 ? cents[cents.length - 1] : 0;
    cents.push(raw - 1200 * Math.round((raw - previous) / 1200));
  }
  return cents;
};

/** MIDI number → frequency (Hz). The inverse of `freqToMidi`. */
export const midiToFreq = (midi: number): number =>
  440 * Math.pow(2, (midi - 69) / 12);

/** MIDI number → note name (`A3`). `midiSolfege` is its Korean-solfège pair. */
export const midiNoteName = (midi: number): string =>
  noteName(midiToFreq(midi));

export const freqToNote = (f: number): NoteInfo => {
  const midi = freqToMidi(f);
  const r = Math.round(midi);
  const cents = Math.round((midi - r) * 100);
  return {
    name: NOTE_NAMES[(r + 1200) % 12],
    octave: Math.floor(r / 12) - 1,
    midi: r,
    cents,
  };
};

/** Snap a frequency to the nearest A (octave of the 440Hz reference). */
export const nearestAFreq = (f: number): number => {
  const k = Math.round((12 * Math.log2(f / 440)) / 12);
  return 440 * Math.pow(2, k);
};

export const noteName = (f: number): string => {
  const n = freqToNote(f);
  return n.name + n.octave;
};

export const semitoneClass = (f: number): number =>
  ((Math.round(12 * Math.log2(f / 440) + 69) % 12) + 12) % 12;

/**
 * 계이름(고정도). 시안이 음이름을 `A3(라)`처럼 **영문 + 계이름** 두 겹으로 쓰기 때문에
 * `noteName`(=`A3`)과 짝으로 필요하다.
 *
 * `NOTE_NAMES`(영문) 옆에 두는 이유: 같은 12음을 두 표기로 부르는 표라 한쪽만 옮기면
 * 색인이 어긋난다. 둘 다 인덱스 0이 C다.
 */
export const SOLFEGE_NAMES = [
  '도',
  '도♯',
  '레',
  '레♯',
  '미',
  '파',
  '파♯',
  '솔',
  '솔♯',
  '라',
  '라♯',
  '시',
] as const;

/** MIDI 번호 → 계이름. 반올림해서 가장 가까운 반음의 이름을 준다. */
export const midiSolfege = (midi: number): string =>
  SOLFEGE_NAMES[((Math.round(midi) % 12) + 12) % 12];
